import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import type { ReactNode } from "react";

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  emptyState?: ReactNode;
  onRowClick?: (row: T) => void;
}

/**
 * Generic table built on TanStack Table. Swiss aesthetic:
 * thin borders, mono numbers in numeric columns (caller's responsibility).
 */
export function DataTable<T>({
  data,
  columns,
  emptyState,
  onRowClick,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border-primary">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="border-b border-border-primary bg-bg-secondary text-left">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  className="px-3 py-2.5 font-medium text-text-secondary"
                  style={{ width: h.column.columnDef.size }}
                >
                  {h.isPlaceholder
                    ? null
                    : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              className={`border-b border-border-secondary last:border-0 transition-colors hover:bg-bg-hover ${
                onRowClick ? "cursor-pointer" : ""
              }`}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2.5 align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
